import { useTranslation } from '@msdk/common'
import { AboutMicroservice } from '@msdk/definitions'
import {
  Box,
  CloseIcon,
  createComponentWrapperWithRef,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  styled,
  Typography,
  uiTokens,
} from '@msdk/ui'
import { PhoneNumberIcon } from 'components/Icons/AboutIcons'
import NoResultIcon from 'components/Icons/NoResultIcon'
import React from 'react'

import { AboutIconRenderer } from './AboutIconRenderer'
import ImporterDetails from './ImporterDetail'
import { formatLineReturn } from './utils'

const StyledPhoneNumberBox = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;
`

const StyledDescriptionMuiBox = styled(Box)`
  padding: 24px;
  width: 100%;
  white-space: pre-wrap;
`
const StyledDescriptionNovaBox = styled(Box)`
  display: flex;
  width: 600px;
  padding: ${uiTokens.spacing.padding.medium}px;
  flex-direction: column;
  align-items: flex-start;
  border-bottom: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
`
const StyledDescriptionBox = createComponentWrapperWithRef(
  {
    mui: StyledDescriptionMuiBox,
    nova: StyledDescriptionNovaBox,
  },
  'StyledDescriptionBox',
)

const StyledMuiDivider = styled(Divider)`
  margin: 0 24px;
`
const StyledNovaDivider = styled('div')`
  display: none;
`
const StyledDivider = createComponentWrapperWithRef<{ children?: React.ReactNode }>(
  {
    mui: StyledMuiDivider,
    nova: StyledNovaDivider,
  },
  'StyledDivider',
)
const StyledNoResultBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const StyledDialogMuiTitle = styled(DialogTitle)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding: 16px 24px;
  max-width: 900px;
`
const StyledDialogNovaTitle = styled(DialogTitle)`
  display: flex;
  height: ${uiTokens.sizing.uiControl.height.large}px;
  width: 600px;
  padding: ${uiTokens.spacing.padding.semiMedium}px ${uiTokens.spacing.padding.semiMedium}px
    ${uiTokens.spacing.padding.small}px ${uiTokens.spacing.padding.medium}px;
  align-items: center;
  gap: ${uiTokens.spacing.gap.small}px;
  justify-content: space-between;
  border-bottom: ${uiTokens.sizing.stroke.light}px solid ${uiTokens.uiStroke.neutral};
`
const StyledDialogTitle = createComponentWrapperWithRef(
  {
    mui: StyledDialogMuiTitle,
    nova: StyledDialogNovaTitle,
  },
  'StyledDialogTitle',
)

const StyledCloseMuiIcon = styled(CloseIcon)`
  cursor: pointer;
  &:hover {
    opacity: 60%;
  }
`
const StyledCloseNovaIcon = styled(CloseIcon)`
  cursor: pointer;
  &:hover {
    opacity: 60%;
  }
  color: ${uiTokens.uiContent.accent};
`
const StyledCloseIcon = createComponentWrapperWithRef(
  {
    mui: StyledCloseMuiIcon,
    nova: StyledCloseNovaIcon,
  },
  'StyledCloseIcon',
)

const StyledUDIBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 170px;
`
const StyledAboutMuiColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-right: 50px;
  width: 400px;
  white-space: pre-wrap;
  justify-content: flex-start;
`
const StyledAboutNovaColumn = styled(Box)`
  display: flex;
  flex: 1 0 0;
  align-items: flex-start;
  gap: 16px;
`
const StyledAboutColumn = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiColumn,
    nova: StyledAboutNovaColumn,
  },
  'StyledAboutColumn',
)

const StyledManufacturerBox = styled(Box)`
  display: flex;
  flex-direction: column;
  max-width: 380px;
  gap: 0;
`

const StyledAboutSectionMuiTitle = styled(Typography)`
  opacity: 60%;
`
const StyledAboutSectionNovaTitle = styled(Typography)`
  color: ${uiTokens.uiContent.neutral};
`
const StyledAboutSectionTitle = createComponentWrapperWithRef(
  {
    mui: StyledAboutSectionMuiTitle,
    nova: StyledAboutSectionNovaTitle,
  },
  'StyledAboutSectionTitle',
)
const StyledAboutMuiDialogContent = styled(DialogContent)`
  width: 900px;
`
const StyledAboutNovaDialogContent = styled(DialogContent)`
  width: 600px;
`
const StyledAboutDialogContent = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiDialogContent,
    nova: StyledAboutNovaDialogContent,
  },
  'StyledAboutDialogContent',
)

const StyledAboutMuiMainBox = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 880px;
  padding: 30px;
  height: fit-content;
`
const StyledAboutNovaMainBox = styled(Box)`
  display: flex;
  flex-direction: row;
  width: 599px;
  padding: ${uiTokens.spacing.padding.medium}px;
  height: fit-content;
  align-items: flex-start;
  gap: 24px;
`
const StyledAboutMainBox = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiMainBox,
    nova: StyledAboutNovaMainBox,
  },
  'StyledAboutMainBox',
)

const StyledAboutMuiItem = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 20px;
`
const StyledAboutNovaItem = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 16px;
`
const StyledAboutItem = createComponentWrapperWithRef(
  {
    mui: StyledAboutMuiItem,
    nova: StyledAboutNovaItem,
  },
  'StyledAboutItem',
)

const StyledIconsBox = styled(Box)`
  padding-left: 24px;
  padding-bottom: 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`

const StyledItemMuiTypography = styled(Typography)`
  max-width: 360px;
  white-space: pre-wrap;
  line-height: 180%;
`
const StyledItemNovaTypography = styled(Typography)`
  color: ${uiTokens.uiContent.emphasized};
  font-weight: ${uiTokens.font.fontWeight.medium};
  white-space: pre-line;
`
const StyledItemTypography = createComponentWrapperWithRef(
  {
    mui: StyledItemMuiTypography,
    nova: StyledItemNovaTypography,
  },
  'StyledItemTypography',
)

const StyledAboutMuiTitleTypography = styled(Typography)`
  max-width: 90%;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  font-weight: 700;
  font-size: 18px;
  line-height: 133%;
`
const StyledAboutNovaTitleTypography = styled(Typography)`
  max-width: 90%;
  white-space: pre-wrap;
  font-size: ${uiTokens.font.fontSize.title.large}px;
  color: ${uiTokens.uiContent.emphasized};
`
const StyledAboutTitleTypography = createComponentWrapperWithRef<{ children: React.ReactNode }>(
  {
    mui: StyledAboutMuiTitleTypography,
    nova: ({ children }) => <StyledAboutNovaTitleTypography variant="h6">{children}</StyledAboutNovaTitleTypography>,
  },
  'StyledAboutTitleTypography',
)

const StyledNovaTypographyDescription = styled(Typography)`
  font-style: normal;
  font-weight: ${uiTokens.font.fontWeight.medium};
  letter-spacing: normal;
  color: ${uiTokens.uiContent.emphasized};
`
const StyledTypographyDescription = createComponentWrapperWithRef<{ children: React.ReactNode }>(
  {
    mui: ({ children }) => <Typography variant="body1">{children}</Typography>,
    nova: ({ children }) => (
      <StyledNovaTypographyDescription variant="body1">{children}</StyledNovaTypographyDescription>
    ),
  },
  'StyledTypographyDescription',
)
interface AboutModalProps {
  open: boolean
  onClose: () => void
  microservice?: AboutMicroservice
}

function AboutModal({ open, onClose, microservice }: AboutModalProps) {
  const t = useTranslation()

  const udi = microservice && microservice.udi
  const udiHasData = udi && (udi.gtin || udi.serialNumber || udi.manufactureDate || udi.version)

  const iconsSectionToShow =
    microservice &&
    (microservice.medicalDevice ||
      microservice.consultInstructionsForUseIcon ||
      microservice.ceApproval ||
      microservice.prescriptionOnlyIcon ||
      microservice.instructionManualMustBeReadIcon)
  const manufacturerHasData =
    microservice &&
    (microservice.manufacturerName || microservice.manufacturerAddress || microservice.manufacturerPhoneNumber)

  const importerHasData =
    microservice &&
    (microservice.importer?.countryIdentifier ||
      microservice.importer?.companyName1 ||
      microservice.importer?.companyName2 ||
      microservice.importer?.address1 ||
      microservice.importer?.address2 ||
      microservice.importer?.zipCode ||
      microservice.importer?.city ||
      microservice.importer?.province ||
      microservice.importer?.country ||
      microservice.importer?.phoneNumber)

  return (
    <Dialog
      keepMounted={false}
      open={open}
      onClose={() => {
        onClose()
      }}
    >
      <StyledDialogTitle>
        <StyledAboutTitleTypography>{microservice && microservice.productName}</StyledAboutTitleTypography>
        <StyledCloseIcon
          onClick={() => {
            onClose()
          }}
        />
      </StyledDialogTitle>
      <StyledAboutDialogContent>
        {microservice?.description && (
          <>
            <StyledDescriptionBox>
              <StyledAboutSectionTitle variant="body2">
                {t('ABOUT_MODAL_DESCRIPTION_LABEL', 'Description')}
              </StyledAboutSectionTitle>
              <StyledTypographyDescription>
                {microservice?.description && formatLineReturn(microservice!.description!)}
              </StyledTypographyDescription>
            </StyledDescriptionBox>
            <StyledDivider />
          </>
        )}
        <StyledAboutMainBox>
          {udiHasData && (
            <StyledAboutColumn>
              <StyledAboutItem>
                {microservice.udiIcon ? <AboutIconRenderer iconType="UDI" /> : <AboutIconRenderer iconType="Spacer" />}
                <StyledUDIBox>
                  {udi && udi.gtin && (
                    <Box>
                      <StyledAboutSectionTitle variant="body2">{t('ABOUT_GTIN', 'GTIN')}</StyledAboutSectionTitle>
                      <StyledItemTypography variant="body1">{udi.gtin}</StyledItemTypography>
                    </Box>
                  )}
                  {udi && udi.manufactureDate && (
                    <Box>
                      <StyledAboutSectionTitle variant="body2">
                        {t('ABOUT_MANUFACTURE_DATE', 'Manufacture date')}
                      </StyledAboutSectionTitle>
                      <StyledItemTypography variant="body1">{udi.manufactureDate}</StyledItemTypography>
                    </Box>
                  )}
                  {udi && udi.version && (
                    <Box>
                      <StyledAboutSectionTitle variant="body2">{t('ABOUT_VERSION', 'Version')}</StyledAboutSectionTitle>
                      <StyledItemTypography variant="body1">{udi.version}</StyledItemTypography>
                    </Box>
                  )}
                  {udi && udi.serialNumber && (
                    <Box>
                      <StyledAboutSectionTitle variant="body2">
                        {t('ABOUT_SERIAL_NUMBER', 'Serial Number')}
                      </StyledAboutSectionTitle>
                      <StyledItemTypography variant="body1">{udi.serialNumber}</StyledItemTypography>
                    </Box>
                  )}
                </StyledUDIBox>
              </StyledAboutItem>
            </StyledAboutColumn>
          )}
          {manufacturerHasData && (
            <StyledAboutColumn>
              <StyledAboutItem>
                <AboutIconRenderer iconType="Manufacturer" />
                <StyledManufacturerBox>
                  <StyledAboutSectionTitle variant="body2">
                    {t('ABOUT_MANUFACTURER', 'Manufacturer')}
                  </StyledAboutSectionTitle>
                  <StyledItemTypography variant="body1">
                    {microservice && formatLineReturn(microservice!.manufacturerName!)}
                  </StyledItemTypography>
                  <StyledItemTypography variant="body1">
                    {microservice && formatLineReturn(microservice!.manufacturerAddress!)}
                  </StyledItemTypography>
                  {microservice && microservice.manufacturerPhoneNumber && (
                    <StyledPhoneNumberBox>
                      <PhoneNumberIcon />
                      <StyledItemTypography variant="body1">
                        {microservice && microservice.manufacturerPhoneNumber}
                      </StyledItemTypography>
                    </StyledPhoneNumberBox>
                  )}
                </StyledManufacturerBox>
              </StyledAboutItem>
            </StyledAboutColumn>
          )}
          {!udiHasData && !manufacturerHasData && (
            <StyledNoResultBox>
              <NoResultIcon />
              <Typography variant="h5" gutterBottom>
                {t('ABOUT_NO_RESULTS', 'Sorry, no information available.')}
              </Typography>
            </StyledNoResultBox>
          )}
        </StyledAboutMainBox>
        {iconsSectionToShow && (
          <StyledIconsBox>
            {microservice && microservice.mdIcon && <AboutIconRenderer iconType="MD" />}
            {microservice && microservice.prescriptionOnlyIcon && !microservice.mdIcon && (
              <AboutIconRenderer iconType="PrescriptionOnly" />
            )}
            {microservice && microservice.consultInstructionsForUseIcon && (
              <AboutIconRenderer iconType="Instructions" />
            )}
            {microservice && microservice.ceApproval && <AboutIconRenderer iconType="CE" />}
            {microservice && microservice.prescriptionOnlyIcon && microservice.mdIcon && (
              <AboutIconRenderer iconType="PrescriptionOnly" />
            )}
            {microservice && microservice.instructionManualMustBeReadIcon && (
              <AboutIconRenderer iconType="ReadInstructions" />
            )}
          </StyledIconsBox>
        )}
        {importerHasData && <StyledDivider />}
        {importerHasData && <ImporterDetails microservice={microservice} />}
      </StyledAboutDialogContent>
    </Dialog>
  )
}

export default AboutModal
